import { useState } from 'react';
import '../../styles/pages/Admin/Dashboard.scss';
import SERVER from '../../hooks/useServer';
import axios from 'axios';
import TranslationButton from './TranslationButton';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AdminSubCategories = ({ activeTab, subCategories, setSubCategories, categories }) => {
    // State declarations
    const [loading, setLoading] = useState(null)
    const [products, setProducts] = useState([]);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Filter and search states
    const [categoryFilter, setCategoryFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPageCommandes, setCurrentPageCommandes] = useState(1);
    // Calculate paginated commandes
    const indexOfLastCommande = currentPageCommandes * itemsPerPage;
    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    
    const filteredSubCategories = subCategories.filter(sub => {
        const matchesSearch = sub.title.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter ? sub.category_id === categoryFilter : true;
        return matchesSearch && matchesCategory;
    });


    // Paginated data
    const currentSubCategories = filteredSubCategories.slice(indexOfFirstItem, indexOfLastItem);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Image modal handlers
    const openImageModal = (imageUrl) => {
        setSelectedImage(imageUrl);
        setIsModalOpen(true);
    };

    // Form states
    const [newSubCategory, setNewSubCategory] = useState({title: '', image: '', category_id: ''});
    
    const handleAddSubCategory = async () => {
        // Validation
        if (!newSubCategory.title.trim()) {
            toast.error('Le nom de la sous-catégorie est requis');
            return;
        }
        if (!newSubCategory.category_id) {
            toast.error('Veuillez sélectionner une catégorie parente');
            return;
        }

        // Check for duplicate subcategory name
        const duplicateSubCategory = subCategories.find(
            sub => sub.title.toLowerCase().trim() === newSubCategory.title.toLowerCase().trim() &&
            sub.category_id === parseInt(newSubCategory.category_id)
        );
        if (duplicateSubCategory) {
            toast.error('Une sous-catégorie avec ce nom existe déjà dans cette catégorie');
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('title', newSubCategory.title);
            formData.append('category_id', newSubCategory.category_id);
            if (newSubCategory.imageFile) {
                formData.append('image', newSubCategory.imageFile);
            }

            const response = await axios.post(`${SERVER}/subcategories`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            setSubCategories([...subCategories, response.data]);
            setNewSubCategory({title: '', image: '', category_id: ''});
            toast.success('Sous-catégorie ajoutée avec succès');
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(errorMsg);
            toast.error(`Erreur: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette sous-catégorie ? Tous les produits associés seront également supprimés.')) {
            return;
        }

        try {
            setLoading(true);
            let endpoint = '';
            switch(type) {
                case 'subCategory':
                    endpoint = `${SERVER}/subcategories/${id}`;
                    break;
                default:
                    return;
            }

            await axios.delete(endpoint);
            
            // Update local state
            switch(type) {
                case 'subCategory':
                    setSubCategories(subCategories.filter(sub => sub.id !== id));
                    setProducts(products.filter(product => product.sub_category_id !== id));
                    toast.success('Sous-catégorie supprimée avec succès');
                    break;
                default:
                    break;
            }
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(errorMsg);
            toast.error(`Erreur lors de la suppression: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (type) => {
        // Validation
        if (!newSubCategory.title.trim()) {
            toast.error('Le nom de la sous-catégorie est requis');
            return;
        }
        if (!newSubCategory.category_id) {
            toast.error('Veuillez sélectionner une catégorie parente');
            return;
        }

        // Check for duplicate subcategory name (excluding current subcategory)
        const duplicateSubCategory = subCategories.find(
            sub => sub.id !== selectedSubCategory.id &&
            sub.title.toLowerCase().trim() === newSubCategory.title.toLowerCase().trim() &&
            sub.category_id === parseInt(newSubCategory.category_id)
        );
        if (duplicateSubCategory) {
            toast.error('Une sous-catégorie avec ce nom existe déjà dans cette catégorie');
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            let response;

            switch(type) {
                case 'subCategory':
                    formData.append('title', newSubCategory.title);
                    formData.append('category_id', newSubCategory.category_id);
                    if (newSubCategory.imageFile) {
                        formData.append('image', newSubCategory.imageFile);
                    } else if (newSubCategory.existing_image) {
                        formData.append('existing_image', newSubCategory.existing_image);
                    }

                    response = await axios.put(`${SERVER}/subcategories/${selectedSubCategory.id}`, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    
                    setSubCategories(subCategories.map(sub => 
                        sub.id === response.data.id ? response.data : sub
                    ));
                    toast.success('Sous-catégorie mise à jour avec succès');
                    break;
                
                default:
                    return;
            }

            setNewSubCategory({title: '', image: '', category_id: ''});
            setIsEditing(false);
            setSelectedSubCategory(null);
            setError(null);
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(errorMsg);
            toast.error(`Erreur: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (type, item) => {
        setIsEditing(true);
        switch(type) {
            case 'subCategory':
                setSelectedSubCategory(item);
                setNewSubCategory({
                    title: item.title,
                    image: item.banner_image,
                    category_id: item.category_id,
                    existing_image: item.banner_image
                });
                toast.info('Mode édition activé');
                break;
            default:
                break;
        }
    };

    const closeImageModal = () => {
        setIsModalOpen(false);
        setSelectedImage(null);
    };


    if (loading) return <div className="loading">Chargement...</div>;
    if (error) return <div className="error">Erreur: {error}</div>;
    return (
        <div className='admin'>
            {isModalOpen && (
                <div className="image-modal" onClick={closeImageModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <span className="close-button" onClick={closeImageModal}>&times;</span>
                        <img src={`${SERVER}/${selectedImage}`} alt="Full size" />
                    </div>
                </div>
            )}
            {/* Search Bar */}
            <div className="search-filter-container">
                <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="search-input"
                />
                
                {activeTab === 'subCategories' && (
                    <select
                        value={categoryFilter}
                        onChange={(e) => {
                            setCategoryFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="filter-select"
                    >
                        <option value="">Toutes les catégories</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>{category.title}</option>
                        ))}
                    </select>
                )}
            </div>

            {/* SubCategories Tab */}
            {activeTab === 'subCategories' && (
                <div className="admin-section">
                    <h2>Sous-catégories</h2>
                    
                    {/* SubCategory Form */}
                    <div className="admin-form">
                        <select
                            value={newSubCategory.category_id}
                            onChange={(e) => setNewSubCategory({...newSubCategory, category_id: e.target.value})}
                        >
                            <option value="">Sélectionner une catégorie parente</option>
                            {categories.map((category) => (
                                <option key={category.id} value={category.id}>{category.title}</option>
                            ))}
                        </select>
                        <input
                            type="text"
                            placeholder="Nom de la sous-catégorie"
                            value={newSubCategory.title}
                            onChange={(e) => setNewSubCategory({...newSubCategory, title: e.target.value})}
                        />
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    setNewSubCategory({
                                        ...newSubCategory,
                                        image: URL.createObjectURL(file),
                                        imageFile: file
                                    });
                                }
                            }}
                        />
                        {isEditing ? (
                            <>
                                <button onClick={() => handleUpdate('subCategory')}>Mettre à jour</button>
                                <button onClick={() => { 
                                    setIsEditing(false); 
                                    setNewSubCategory({title: '', image: '', category_id: ''}); 
                                }}>Annuler</button>
                            </>
                        ) : (
                            <button onClick={handleAddSubCategory}>Ajouter une sous-catégorie</button>
                        )}
                    </div>
                    
                    {/* SubCategories Table */}
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '100px' }}>Image</th>
                                    <th>Nom</th>
                                    <th>Catégorie parente</th>
                                    <th style={{ width: '200px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentSubCategories.map((subCategory) => {
                                    const parentCategory = categories.find(cat => cat.id === subCategory.category_id);
                                    return (
                                        <tr key={subCategory.id}>
                                            <td>
                                                {subCategory.banner_image && (
                                                    <img 
                                                        src={`${SERVER}/${subCategory.banner_image}`} 
                                                        alt={subCategory.title} 
                                                        className="table-image"
                                                        onClick={() => openImageModal(subCategory.banner_image)}
                                                    />
                                                )}
                                            </td>
                                            <td>{subCategory.title}</td>
                                            <td>{parentCategory?.title || 'N/A'}</td>
                                            <td>
                                                <div className="admin-actions">
                                                    <button onClick={() => handleEdit('subCategory', subCategory)}>Modifier</button>
                                                    <button onClick={() => handleDelete('subCategory', subCategory.id)}>Supprimer</button>
                                                </div>
                                                <TranslationButton
                                                    data={subCategory}
                                                    type="subcategory"
                                                    onTranslationSaved={() => {
                                                        window.location.reload();
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        
                        {/* Pagination */}
                        <div className="pagination">
                            {Array.from({ length: Math.ceil(filteredSubCategories.length / itemsPerPage) }).map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => paginate(index + 1)}
                                    className={currentPage === index + 1 ? 'active' : ''}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSubCategories;